

















































































































































import {
  Vue,
  Component,
  Watch
} from "@/shared/vuepack";
import { Validator } from "vee-validate";
import Input from "@/shared/components/ui/Input.vue";
import Button from "@/shared/components/ui/Button.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import {
  HealthcareExpenseForecastRequest,
  FetchState,
  PlanType,
  HouseholdMember,
  MemberType
} from "@/shared/types";
import Widget from "@/shared/components/Widget.vue";
import KeyValuePair from "@bend-financial/styleguide";
import * as _ from "lodash";
import moment from "moment";
import { formatCurrency } from "@/shared/currency";
import ContributionPlannerSidebar from "@/employee/components/contributionPlanner/ContributionPlannerSidebar.vue";
import ContributionPlannerSummary from "@/employee/components/contributionPlanner/ContributionPlannerSummary.vue";
import FamilyMemberRow from "@/employee/components/contributionPlanner/FamilyMemberRow.vue";
import EditFamilyMember from "@/employee/components/contributionPlanner/EditFamilyMember.vue";
import * as routes from "@/employee/route-names";
import { useContributionPlannerStore } from "@/employee/store/contributionPlanner";
import { useContactInfoStore } from "@/shared/store/contactinfo";
import { useHsaTotalStore } from "@/shared/store/hsaTotal";
import { useHsaDailyBalanceStore } from "@/shared/store/hsaDailyBalance";
import { useBirthYearStore } from "@/employee/store/birthYear";
import { useHsaLimitStore } from "@/shared/store/hsalimit";
import { useInvestmentDailyBalanceStore } from "@/shared/store/investmentDailyBalance";
import { useInvestmentThresholdStore } from "@/shared/store/investmentThreshold";
import { useUserPlanStore } from "@/employee/store/userPlan";
import { useNotifierStore } from "@/shared/store/notifier";
import { v4 as uuid } from "uuid";
import { useCustodianSettingsStore } from "@/shared/store/custodianSettings";

const initialState: HealthcareExpenseForecastRequest = {
  birthYear: null,
  zipCode: null,
  sex: "",
  persona: "",
  usesTobacco: false,
  deductibleAmount: 0,
  maxOutOfPocket: 0,
  premium: null,
  retirementAge: null,
  currentHsaBalance: null,
  currentInvestmentBalance: null,
  employerHsaContributions: null,
  employeeHsaContributions: 0,
  conditions: [],
  minimumInvestmentBalance: null,
  familyMembers: []
};

const emptyHouseholdMemberModel: HouseholdMember = {
  familyMemberId: 0,
  memberType: null,
  firstName: "",
  lastName: "",
  birthYear: null,
  sex: "",
  usesTobacco: false,
  conditions: [],
  key: ""
};

@Component({
  components: {
    Button,
    Widget,
    Input,
    ContributionPlannerSidebar,
    ContributionPlannerSummary,
    FamilyMemberRow,
    EditFamilyMember,
    GenericLoadingSpinner
  }
})
export default class ContributionPlanner extends Vue {
  $validator: Validator;

  contributionPlannerStore = useContributionPlannerStore();
  contactInfoStore = useContactInfoStore();
  hsaTotalStore = useHsaTotalStore();
  hsaBalanceStore = useHsaDailyBalanceStore();
  birthYearStore = useBirthYearStore();
  hsaLimitStore = useHsaLimitStore();
  investmentStore = useInvestmentDailyBalanceStore();
  investmentThresholdStore = useInvestmentThresholdStore();
  userPlanStore = useUserPlanStore();
  notifierStore = useNotifierStore();
  custodianSettingsStore = useCustodianSettingsStore();

  formModel: HealthcareExpenseForecastRequest = { ...initialState };
  newMemberModel = { ...emptyHouseholdMemberModel };
  newMemberFormVisible = { value: false };
  isSubmitting = false;
  retirementAges = [];
  formatCurrency = formatCurrency;
  learnMoreVisible = false;
  routes = routes;
  PlanType = PlanType;
  tooltipPlacement = "right";

  mounted () {
    this.userPlanStore.getPlanType();
    this.setLearnMoreVisibility();
    this.contributionPlannerStore.loadForecastRequest();
    this.contributionPlannerStore.loadConditions();
    this.hsaLimitStore.getHsaAccountLimits();
    this.birthYearStore.loadBirthYear().then(() => this.updateRetirementAges());
    this.contactInfoStore.getContactInformation();
    this.hsaTotalStore.getUserPayrollSummary();
    this.hsaBalanceStore.loadBalanceOnFile();
    this.investmentStore.loadCurrentDailyBalance();
    this.contributionPlannerStore.loadHsaForecast();
    this.investmentThresholdStore.loadInvestmentThresholds();
    window.addEventListener("resize", this.debouncedUpdateTooltipPlacement);
    this.updateTooltipPlacement();
  }

  debouncedUpdateTooltipPlacement = _.debounce(() => this.updateTooltipPlacement(), 250);

  updateTooltipPlacement () {
    if (window.innerWidth < 700) {
      this.tooltipPlacement = "top";
    } else {
      this.tooltipPlacement = "right";
    }
  }

  beforeDestroy () {
    window.removeEventListener("resize", this.debouncedUpdateTooltipPlacement);
  }

  get portalFunctionalityDisabled (): boolean {
    return this.custodianSettingsStore.portalFunctionalityDisabled;
  }

  @Watch("contributionPlannerStore.forecastRequest", { deep: true })
  onForecastRequestStateChanged () {
    if (this.contributionPlannerStore.isForecastRequestLoaded && this.contributionPlannerStore.forecastRequest) {
      this.cloneToCurrentModel(this.contributionPlannerStore.forecastRequest);
    }
  }

  cloneToCurrentModel (o) {
    const clonedModel = _.cloneDeep(o);
    this.formModel = clonedModel;
    this.formModel.familyMembers = this.formModel.familyMembers.map(m => ({ ...m, key: uuid() }));
  }

  get showCalculator (): boolean {
    return (
        this.contributionPlannerStore.isForecastRequestLoaded &&
        this.contributionPlannerStore.isConditionsLoaded &&
        !this.learnMoreVisible
    );
  }

  get mappedConditions (): KeyValuePair[] {
    return this.contributionPlannerStore.conditions
        .map(condition => {
          return {
            key: condition.conditionName,
            value: condition.conditionCode
          };
        })
        .sort((a, b) => a.key.localeCompare(b.key));
  }

  get contributionTooltip (): string {
    if (this.hsaTotalStore.employerAmount && this.hsaTotalStore.employerAmount !== 0) {
      return `Your employer contributes ${formatCurrency(
          this.hsaTotalStore.employerAmount)} so you can only contribute ${formatCurrency(
          this.hsaContributionLimit)}. This simulation assumes that the contribution amount will grow by 3% each year.`;
    } else {
      return `This simulation assumes that the contribution amount will grow by 3% each year.`;
    }
  }

  get hsaContributionLimit (): number {
    return this.hsaLimitStore.getHsaLimitByYear(moment().year()) - this.hsaTotalStore.employerAmount;
  }

  get isSubmitEnabled (): boolean {
    if (this.portalFunctionalityDisabled) {
      return false;
    }

    return (
        !this.isSubmitting &&
        this.contactInfoStore.contactInformationState === FetchState.Loaded &&
        this.hsaBalanceStore.balanceOnFileLoaded &&
        this.hsaTotalStore.isLoaded &&
        this.birthYearStore.birthYearLoaded &&
        this.hsaLimitStore.fetchState === FetchState.Loaded
    );
  }

  get hsaForecastRetirementAge (): number {
    const retirementYear = this.contributionPlannerStore.hsaForecast.hsaForecasts[
    this.contributionPlannerStore.hsaForecast.hsaForecasts.length - 1
        ].year;
    return retirementYear - this.birthYearStore.birthYear;
  }

  get validateContributionAmount () {
    return {
      required: true,
      decimal: 2,
      min_value_usd: 0,
      max_value_usd: this.hsaContributionLimit
    };
  }

  deductibleValidator () {
    return "required|min_value:0|max_value:" + this.formModel.maxOutOfPocket;
  }

  setLearnMoreVisibility () {
    if (this.$route.name === routes.ContributionPlannerHowItWorks) {
      this.learnMoreVisible = true;
    }
  }

  learnMoreClicked () {
    this.$router.push({ name: routes.ContributionPlannerHowItWorks });
  }

  updateRetirementAges () {
    const currentAge = moment().year() - this.birthYearStore.birthYear;
    let maxRetirementAge = 80;

    if (currentAge >= 80) {
      maxRetirementAge = currentAge + 5;
    }

    for (let age = currentAge + 1; age <= maxRetirementAge; age++) {
      this.retirementAges.push(age);
    }
  }

  showNewMemberForm () {
    this.newMemberModel = { ...emptyHouseholdMemberModel, key: uuid() };
    this.newMemberFormVisible.value = true;
  }

  onDeleteMember (member: any) {
    const deleteIndex = this.formModel.familyMembers.findIndex(m => m.key === member.key);
    if (deleteIndex > -1) {
      this.formModel.familyMembers.splice(deleteIndex, 1);
    }
  }

  onSaveNewMember () {
    this.formModel.familyMembers.push(this.newMemberModel);
    this.newMemberFormVisible.value = false;
  }

  onAbortNewMember () {
    this.newMemberFormVisible.value = false;
  }

  onSubmit (): void {
    if (this.portalFunctionalityDisabled) {
      return;
    }

    this.$validator.validateAll().then(result => {
      if (!result) {
        return;
      }

      if (this.formModel.familyMembers.filter(m => m.memberType === MemberType.Spouse).length > 1) {
        this.notifierStore.setDanger("There must be at most one spouse in a household.");
        return;
      }

      if (this.isSubmitting) {
        return;
      }

      this.isSubmitting = true;

      this.formModel.zipCode = this.contactInfoStore.contactInformation.physicalAddress.zip as any;
      this.formModel.employerHsaContributions = this.hsaTotalStore.employerAmount;
      this.formModel.currentHsaBalance = this.hsaBalanceStore.balanceOnFileAmount;
      this.formModel.currentInvestmentBalance = isNaN(this.investmentStore.singleOrDefaultCurrentBalance.amount)
          ? 0
          : this.investmentStore.singleOrDefaultCurrentBalance.amount;
      this.formModel.birthYear = this.birthYearStore.birthYear;
      this.formModel.minimumInvestmentBalance = _.isNumber(this.investmentThresholdStore.investmentThreshold())
          ? this.investmentThresholdStore.investmentThreshold()
          : 0;

      this.contributionPlannerStore.updateHsaForecast({ ...this.formModel }).then(() => this.isSubmitting = false);
    });
  }
}
